import { and, eq, type InitialQueryBuilder, isNull, isUndefined, not } from "@tanstack/db";

import type { NotificationFeedPreference, WsFeed } from "@storyboard/web-client";
import { membershipIsOffline, membershipIsOnline } from "@/store/availabilityPredicates";
import { chainComparators, compareAsc, compareDesc } from "@/store/compare";
import {
  availabilityForMembership,
  feedHasUnread,
  latestAliasActivityForFeed,
  latestItemForFeed,
  preferenceForFeed,
} from "@/store/feedSelectors";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Account, Feed, WorkspaceMembership } from "@/store/types";

/** PostgreSQL -infinity/infinity round-trip as strings but are invalid in JS Date parsing. */
function pgTimestamptzFieldOrNull(value: string | null | undefined): string | null {
  if (value == null || value === "") return null;
  if (value === "-infinity" || value === "infinity") return null;
  return value;
}

const EPOCH = "1970-01-01T00:00:00.000Z";
const NEG_INFINITY = "-infinity";

export async function markPermissionsLoaded(feedId: string): Promise<void> {
  store.update("feed", feedId, (row) => ({ ...row, loadedPermissions: true }));
}

/**
 * Clears the feed page flags a boot can no longer trust: `loadedLastPage` on every boot, `loadedFirstPage` as well on
 * a full one.
 *
 * `loadedLastPage` says the store held the feed's newest messages when it was set, which stays true only while the
 * realtime subscription keeps the store current. A warm boot restores it from a snapshot that predates everything
 * sent while the app was closed, so left set it makes warmup skip exactly the feeds that need catching up (WEB-2355).
 * `loadedFirstPage` and the contiguity span describe history the store already holds, which a reload does not change.
 */
export function resetFeedPageFlagsForBoot(mode: "full" | "incremental"): void {
  const keepFirstPage = mode === "incremental";
  store.updateMany("feed", store.getAll("feed").keys(), (row) => {
    const loadedFirstPage = keepFirstPage && row.loadedFirstPage;
    // Nothing to clear: the same reference keeps the row's listeners quiet.
    if (!row.loadedLastPage && loadedFirstPage === row.loadedFirstPage) return row;
    return { ...row, loadedFirstPage, loadedLastPage: false };
  });
}

export async function bulkUpsertFeed(records: WsFeed[]) {
  if (records.length === 0) return;
  const data = records.map(
    (r): Feed => ({
      id: r.id,
      createdAt: r.createdAt ?? null,
      updatedAt: r.updatedAt ?? null,
      workspaceId: r.workspaceId ?? null,
      readOnly: r.readOnly,
      lastOpened: pgTimestamptzFieldOrNull(r.lastOpened),
      latestActivity: pgTimestamptzFieldOrNull(r.latestActivity),
      loadedFirstPage: false,
      loadedLastPage: false,
      loadedEvents: false,
      loadedPermissions: false,
      oldestContiguousCreatedAt: null,
      newestContiguousCreatedAt: null,
      feedType: r.feedType ?? "userCreated",
      title: r.title || null,
      isPrivate: r.isPrivate,
      autoJoin: r?.autoJoin,
      isDm: r.isDm,
      isSilent: r?.isSilent,
      aliasMembershipId: ((r as unknown as Record<string, unknown>).aliasMembershipId as string | null) ?? null,
      isAliasFeed: ((r as unknown as Record<string, unknown>).isAliasFeed as boolean) ?? false,
    }),
  );
  // The old ON CONFLICT: page flags reset, loadedPermissions and createdAt preserved,
  // aliasMembershipId coalesces onto the existing value, isAliasFeed can only turn on.
  store.upsert("feed", data, (existing, next) => ({
    ...next,
    createdAt: existing.createdAt,
    loadedPermissions: existing.loadedPermissions,
    oldestContiguousCreatedAt: existing.oldestContiguousCreatedAt,
    newestContiguousCreatedAt: existing.newestContiguousCreatedAt,
    aliasMembershipId: next.aliasMembershipId ?? existing.aliasMembershipId,
    isAliasFeed: next.isAliasFeed ? next.isAliasFeed : existing.isAliasFeed,
  }));
}

/** Feeds on which the membership holds an enabled permission of the given name. */
function feedsWithPermission(workspaceMembershipId: string, permissionName: string): Feed[] {
  const feeds: Feed[] = [];
  const seen = new Set<string>();
  for (const permission of rowsByIndex("permission", idx.permissionsByMembership, workspaceMembershipId)) {
    if (permission.name !== permissionName || !permission.enabled || permission.feedId == null) continue;
    if (seen.has(permission.feedId)) continue;
    seen.add(permission.feedId);
    const feed = store.get("feed", permission.feedId);
    if (feed) feeds.push(feed);
  }
  return feeds;
}

export type GroupedFeedRow = {
  id: string;
  title: string | null;
  workspaceId: string | null;
  updatedAt: string | null;
  groupId: string;
  readOnly: boolean;
  alias: string | null;
  permissionName: string;
};

export const groupedFeedsQuery =
  ({
    myCurrentWorkspaceMembershipId,
    allowNonAliasChannels,
    permissionName = "read",
  }: {
    myCurrentWorkspaceMembershipId: string;
    allowNonAliasChannels?: boolean;
    permissionName?: "read" | "write";
  }) =>
  (q: InitialQueryBuilder) =>
    q
      .from({ permission: store.collection("permission") })
      .innerJoin({ feed: store.collection("feed") }, ({ permission, feed }) => eq(permission.feedId, feed.id))
      .innerJoin({ groupMembership: store.collection("feedGroupMembership") }, ({ feed, groupMembership }) =>
        eq(feed.id, groupMembership.feedId),
      )
      .leftJoin({ alias: store.collection("workspaceCommandAlias") }, ({ feed, alias }) => eq(feed.id, alias.feedId))
      .where(({ permission, alias }) => {
        const scoped = and(
          eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
          eq(permission.name, permissionName),
          eq(permission.enabled, true),
        );
        return allowNonAliasChannels ? scoped : and(scoped, not(isUndefined(alias.id)));
      })
      .select(({ feed, groupMembership, alias }) => ({
        id: feed.id,
        title: feed.title,
        workspaceId: feed.workspaceId,
        updatedAt: feed.updatedAt,
        groupId: groupMembership.groupId,
        readOnly: feed.readOnly,
        alias: alias.alias,
        permissionName,
      }))
      .distinct();

export type FeedIHaveAccessToRow = {
  id: string;
  title: string | null;
  isAliasFeed: boolean;
  aliasMembershipId: string | null;
  groupId: string | null;
};

export const feedsIHaveAccessToQuery =
  ({
    workspaceId,
    workspaceMembershipId,
    permissionName,
    allowNonAliasChannels,
    allowGroups,
  }: {
    workspaceId: string;
    workspaceMembershipId: string;
    permissionName: "read" | "write";
    allowNonAliasChannels: boolean;
    allowGroups: boolean;
  }) =>
  (q: InitialQueryBuilder) =>
    q
      .from({ permission: store.collection("permission") })
      .innerJoin({ feed: store.collection("feed") }, ({ permission, feed }) => eq(permission.feedId, feed.id))
      .leftJoin({ groupMembership: store.collection("feedGroupMembership") }, ({ feed, groupMembership }) =>
        eq(feed.id, groupMembership.feedId),
      )
      .where(({ permission, feed, groupMembership }) => {
        const scoped = and(
          eq(permission.workspaceMembershipId, workspaceMembershipId),
          eq(permission.name, permissionName),
          eq(permission.enabled, true),
          eq(feed.workspaceId, workspaceId),
          allowGroups ? not(isUndefined(groupMembership.groupId)) : isUndefined(groupMembership.groupId),
        );
        return allowNonAliasChannels ? scoped : and(scoped, not(isNull(feed.aliasMembershipId)));
      })
      .select(({ feed, groupMembership }) => ({
        id: feed.id,
        title: feed.title,
        isAliasFeed: feed.isAliasFeed,
        aliasMembershipId: feed.aliasMembershipId,
        groupId: groupMembership.groupId,
      }))
      .distinct()
      .orderBy(({ feed }) => feed.title, { direction: "asc", nulls: "last" });

export type MyActiveFeedRow = {
  id: string;
  title: string | null;
  workspaceId: string | null;
  updatedAt: string | null;
  groupId: string | null;
  readOnly: boolean;
  aliasMembershipId: string | null;
  permissionName: string;
  latestActivity: string;
  latestAliasActivity: string;
  aliasCount: number;
};

export const myActiveFeedsCollections = [
  "feed",
  "permission",
  "feedGroupMembership",
  "workspaceCommandAlias",
  "item",
] as const;

/** Stays a compute pipeline: per-feed alias counts and latest-alias-item aggregates. */
export function computeMyActiveFeeds({
  workspaceId,
  myCurrentWorkspaceMembershipId,
  filterCommandAlias,
  permissionName = "read",
}: {
  workspaceId: string;
  myCurrentWorkspaceMembershipId: string;
  filterCommandAlias?: boolean;
  permissionName?: "read" | "write";
}): MyActiveFeedRow[] {
  const rows: MyActiveFeedRow[] = [];
  for (const feed of feedsWithPermission(myCurrentWorkspaceMembershipId, permissionName)) {
    if (feed.workspaceId !== workspaceId || feed.isDm) continue;
    const aliasCount = idx.commandAliasesByFeed.keys(feed.id).size;
    if (filterCommandAlias && aliasCount === 0) continue;
    const groupMembership = rowsByIndex("feedGroupMembership", idx.feedGroupMembershipsByFeed, feed.id)[0] ?? null;
    rows.push({
      id: feed.id,
      title: feed.title,
      workspaceId: feed.workspaceId,
      updatedAt: feed.updatedAt,
      groupId: groupMembership?.groupId ?? null,
      readOnly: feed.readOnly,
      aliasMembershipId: feed.aliasMembershipId,
      permissionName,
      latestActivity: feed.latestActivity ?? EPOCH,
      latestAliasActivity: latestAliasActivityForFeed(feed.id) ?? EPOCH,
      aliasCount,
    });
  }
  rows.sort(
    chainComparators(
      (a, b) => compareDesc(a.latestAliasActivity, b.latestAliasActivity),
      (a, b) => compareDesc(a.latestActivity, b.latestActivity),
    ),
  );
  return rows;
}

export type TopFeedByActivityRow = { id: string; latestActivity: string; latestAliasActivity: string };

export const topFeedsByActivityCollections = ["feed", "permission", "feedPreference", "item"] as const;

/** Stays a compute pipeline: latest-alias-item aggregates per feed. */
export function computeTopFeedsByActivity({
  workspaceId,
  myCurrentWorkspaceMembershipId,
  limit,
  requireNotificationPreference = false,
}: {
  workspaceId: string;
  myCurrentWorkspaceMembershipId: string;
  limit: number;
  requireNotificationPreference?: boolean;
}): TopFeedByActivityRow[] {
  const rows: TopFeedByActivityRow[] = [];
  for (const feed of feedsWithPermission(myCurrentWorkspaceMembershipId, "read")) {
    if (feed.workspaceId !== workspaceId || feed.isDm) continue;
    if (
      requireNotificationPreference &&
      preferenceForFeed(feed.id, myCurrentWorkspaceMembershipId, { notifyingOnly: true }) === null
    ) {
      continue;
    }
    rows.push({
      id: feed.id,
      latestActivity: feed.latestActivity ?? EPOCH,
      latestAliasActivity: latestAliasActivityForFeed(feed.id) ?? EPOCH,
    });
  }
  rows.sort(
    chainComparators(
      (a, b) => compareDesc(a.latestAliasActivity, b.latestAliasActivity),
      (a, b) => compareDesc(a.latestActivity, b.latestActivity),
    ),
  );
  return rows.slice(0, limit);
}

export type ChannelWithPermissionsRow = Feed & {
  permission_name: string;
  wsmid: string | null;
  name: string | null;
  email: string | null;
};

/** Channels the membership can write to, with the member's account fields riding along. */
export const channelsWithPermissionsQuery =
  (workspaceId: string, workspaceMembershipId: string) => (q: InitialQueryBuilder) =>
    q
      .from({ permission: store.collection("permission") })
      .innerJoin({ feed: store.collection("feed") }, ({ permission, feed }) => eq(permission.feedId, feed.id))
      .innerJoin({ membership: store.collection("workspaceMembership") }, ({ permission, membership }) =>
        eq(permission.workspaceMembershipId, membership.id),
      )
      .innerJoin({ account: store.collection("account") }, ({ membership, account }) =>
        eq(membership.accountId, account.id),
      )
      .where(({ permission, feed }) =>
        and(
          eq(permission.workspaceMembershipId, workspaceMembershipId),
          eq(permission.name, "write"),
          eq(permission.enabled, true),
          eq(feed.workspaceId, workspaceId),
        ),
      )
      .select(({ permission, feed, account }) => ({
        id: feed.id,
        createdAt: feed.createdAt,
        updatedAt: feed.updatedAt,
        workspaceId: feed.workspaceId,
        readOnly: feed.readOnly,
        lastOpened: feed.lastOpened,
        latestActivity: feed.latestActivity,
        loadedFirstPage: feed.loadedFirstPage,
        loadedLastPage: feed.loadedLastPage,
        loadedEvents: feed.loadedEvents,
        loadedPermissions: feed.loadedPermissions,
        oldestContiguousCreatedAt: feed.oldestContiguousCreatedAt,
        newestContiguousCreatedAt: feed.newestContiguousCreatedAt,
        feedType: feed.feedType,
        title: feed.title,
        isPrivate: feed.isPrivate,
        autoJoin: feed.autoJoin,
        isDm: feed.isDm,
        isSilent: feed.isSilent,
        aliasMembershipId: feed.aliasMembershipId,
        isAliasFeed: feed.isAliasFeed,
        permission_name: permission.name,
        wsmid: permission.workspaceMembershipId,
        name: account.name,
        email: account.email,
      }))
      .distinct()
      .orderBy(({ feed }) => feed.isDm, { direction: "asc" })
      .orderBy(({ feed }) => feed.title, { direction: "asc", nulls: "last" });

export const fetchAllGroupedChannelsQuery = async ({
  workspaceMembershipId,
  groupId,
}: {
  workspaceMembershipId: string;
  groupId: string;
}): Promise<{ id: string }[]> => {
  if (!workspaceMembershipId || !groupId) return [];
  const ids: { id: string }[] = [];
  for (const groupMembership of rowsByIndex("feedGroupMembership", idx.feedGroupMembershipsByGroup, groupId)) {
    const feed = store.get("feed", groupMembership.feedId);
    if (!feed) continue;
    const hasRead = rowsByIndex("permission", idx.permissionsByFeed, feed.id).some(
      (p) => p.workspaceMembershipId === workspaceMembershipId && p.name === "read" && p.enabled,
    );
    if (!hasRead) continue;
    const hasUnreadItem = rowsByIndex("item", idx.itemsByFeed, feed.id).some((item) => item.unread === true);
    if (hasUnreadItem) ids.push({ id: feed.id });
  }
  return ids;
};

export const fetchAllNonGroupedChannelsQuery = async ({
  workspaceMembershipId,
}: {
  workspaceMembershipId: string;
}): Promise<{ id: string }[]> => {
  if (!workspaceMembershipId) return [];
  const ids: { id: string }[] = [];
  for (const permission of rowsByIndex("permission", idx.permissionsByMembership, workspaceMembershipId)) {
    if (permission.name !== "read" || !permission.enabled || permission.feedId == null) continue;
    const feed = store.get("feed", permission.feedId);
    if (!feed) continue;
    if (rowsByIndex("feedGroupMembership", idx.feedGroupMembershipsByFeed, feed.id).length > 0) continue;
    const hasUnreadItem = rowsByIndex("item", idx.itemsByFeed, feed.id).some((item) => item.unread === true);
    if (hasUnreadItem) ids.push({ id: feed.id });
  }
  return ids;
};

export const nonGroupedChannelsQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ feed: store.collection("feed") })
    .leftJoin({ groupMembership: store.collection("feedGroupMembership") }, ({ feed, groupMembership }) =>
      eq(feed.id, groupMembership.feedId),
    )
    .where(({ feed, groupMembership }) => and(eq(feed.workspaceId, workspaceId), isUndefined(groupMembership.groupId)))
    .select(({ feed }) => ({ id: feed.id, title: feed.title }));

export type ChannelOrderType = "driver-activity" | "alpha-asc";
export type ChannelTypeType = "my-channels" | "my-unreads" | "all-channels" | "all-unreads";
export type ChannelList = {
  id: string;
  title: string;
  workspaceId: string;
  isPrivate: boolean;
  enabled: boolean;
  groupId: string;
  groupName: string;
  subscribed: boolean;
  name: string;
  unread: boolean;
  aliasChannel: boolean;
  aliasMembershipId: string | null;
  pushNotificationPreference: NotificationFeedPreference;
  latestActivity?: string;
  hasMultipleAlias?: boolean;
  driverActivity?: string;
  unifiedTimestamp?: string;
  aliasOwnerAccountId?: string;
  status?: string;
  disabled?: boolean;
  aliasCount?: number;
};

function channelFilterMatches(filters: string[], availability: ReturnType<typeof availabilityForMembership>): boolean {
  return filters.some((filter) => {
    switch (filter) {
      case "driving":
        return availability?.status === "driving" && membershipIsOnline(availability);
      case "not-driving":
        return (
          availability?.status === "not-driving" &&
          membershipIsOnline(availability) &&
          availability.mostRecentHandsFreeStatusEnabled !== false &&
          availability.mostRecentVolumeAudible !== false
        );
      case "offline":
        return membershipIsOffline(availability);
      case "volume-muted":
        return availability?.mostRecentVolumeAudible === false && membershipIsOnline(availability);
      case "hands-free-off":
        return availability?.mostRecentHandsFreeStatusEnabled === false && membershipIsOnline(availability);
      default:
        return false;
    }
  });
}

export type ChannelListParams = {
  channelOrder: ChannelOrderType;
  channelType: ChannelTypeType;
  workspaceId: string;
  searchValue: string;
  myCurrentWorkspaceMembership: WorkspaceMembership;
  myAccount: Account;
  channelFilters: string[] | null;
  selectedFeedGroups?: string[];
};

export const channelListCollections = [
  "feed",
  "permission",
  "feedPreference",
  "feedGroupMembership",
  "feedGroup",
  "workspaceMembership",
  "workspaceCommandAlias",
  "wsMembershipAvailability",
  "item",
] as const;

/** Stays a compute pipeline: search, availability filters, and per-feed latest-item aggregates. */
export function computeChannelList(params: ChannelListParams) {
  const { channelOrder, channelType, workspaceId, searchValue, myCurrentWorkspaceMembership, channelFilters } = params;
  const workspaceMembershipId = myCurrentWorkspaceMembership?.id;
  const search = searchValue && searchValue.length > 0 ? searchValue.toLowerCase() : null;

  const rows: Array<
    Omit<
      ChannelList,
      "unread" | "subscribed" | "name" | "enabled" | "groupId" | "groupName" | "pushNotificationPreference"
    > & {
      unread: string | null;
      subscribed: boolean | null;
      name: string;
      enabled: boolean;
      groupId: string | null;
      groupName: string | null;
      offline: boolean | null;
      pushNotificationPreference: string | null;
    }
  > = [];

  for (const feed of feedsWithPermission(workspaceMembershipId, "read")) {
    if (feed.isDm || feed.title == null) continue;

    const groupMembership = rowsByIndex("feedGroupMembership", idx.feedGroupMembershipsByFeed, feed.id)[0] ?? null;
    const group = groupMembership ? (store.get("feedGroup", groupMembership.groupId) ?? null) : null;

    if (
      search !== null &&
      !(feed.title?.toLowerCase().includes(search) || group?.name?.toLowerCase().includes(search))
    ) {
      continue;
    }
    if (params.selectedFeedGroups && params.selectedFeedGroups.length > 0) {
      const inSelected = groupMembership != null && params.selectedFeedGroups.includes(groupMembership.groupId);
      const allowNonGrouped = params.selectedFeedGroups.includes("non-grouped-channels") && groupMembership == null;
      if (!inSelected && !allowNonGrouped) continue;
    }

    const availability = availabilityForMembership(feed.aliasMembershipId);
    if (channelFilters && channelFilters.length > 0 && !channelFilterMatches(channelFilters, availability)) {
      continue;
    }

    const preference = preferenceForFeed(feed.id, workspaceMembershipId);
    const notifying = preference != null && preference.pushNotificationPreference !== "none";
    if ((channelType === "my-channels" || channelType === "my-unreads") && !notifying) continue;

    const items = rowsByIndex("item", idx.itemsByFeed, feed.id).filter((item) => item.deletedAt == null);
    let latestCreatedAt: string | null = null;
    let latestAliasCreatedAt: string | null = null;
    let latestUnreadCreatedAt: string | null = null;
    for (const item of items) {
      if (latestCreatedAt === null || item.createdAt > latestCreatedAt) latestCreatedAt = item.createdAt;
      if (item.isFromAliasMember === true && (latestAliasCreatedAt === null || item.createdAt > latestAliasCreatedAt)) {
        latestAliasCreatedAt = item.createdAt;
      }
      if (item.unread === true && (latestUnreadCreatedAt === null || item.createdAt > latestUnreadCreatedAt)) {
        latestUnreadCreatedAt = item.createdAt;
      }
    }
    if (channelType === "my-unreads" && latestUnreadCreatedAt === null) continue;

    const driverActivity = latestAliasCreatedAt ?? NEG_INFINITY;
    const latestActivity =
      feed.updatedAt != null && (latestCreatedAt == null || feed.updatedAt > latestCreatedAt)
        ? feed.updatedAt
        : latestCreatedAt;
    const unifiedTimestamp = feed.isAliasFeed ? driverActivity : (latestActivity ?? null);
    const aliasMembership = store.get("workspaceMembership", feed.aliasMembershipId);
    const aliasMemberships = new Set<string>();
    for (const aliasKey of idx.commandAliasesByFeed.keys(feed.id)) {
      const alias = store.get("workspaceCommandAlias", aliasKey);
      if (alias && alias.workspaceId === workspaceId) aliasMemberships.add(alias.workspaceMembershipId);
    }

    rows.push({
      id: feed.id,
      title: feed.title,
      workspaceId: feed.workspaceId ?? "",
      isPrivate: feed.isPrivate,
      subscribed: preference ? preference.pushNotificationPreference !== "none" : null,
      pushNotificationPreference: preference?.pushNotificationPreference ?? null,
      enabled: true,
      name: "read",
      groupId: groupMembership?.groupId ?? null,
      groupName: group?.name ?? null,
      aliasChannel: feed.isAliasFeed,
      aliasMembershipId: feed.aliasMembershipId,
      unifiedTimestamp: unifiedTimestamp ?? undefined,
      driverActivity,
      latestActivity: latestActivity ?? undefined,
      unread: latestUnreadCreatedAt,
      aliasOwnerAccountId: aliasMembership?.accountId ?? undefined,
      status: availability?.status ?? undefined,
      offline: availability?.offline ?? null,
      aliasCount: aliasMemberships.size,
    });
  }

  if (channelOrder === "driver-activity") {
    rows.sort((a, b) => compareDesc(a.unifiedTimestamp ?? null, b.unifiedTimestamp ?? null));
  } else {
    rows.sort((a, b) => compareAsc(a.title?.toLowerCase(), b.title?.toLowerCase()));
  }
  return rows;
}

export type NonAliasChannelRow = {
  id: string;
  title: string;
  unread: boolean;
  subscribed: boolean;
};

export const nonAliasChannelsCollections = ["feed", "permission", "feedPreference", "item"] as const;

/** Stays a compute pipeline: per-feed unread and latest-item aggregates. */
export function computeNonAliasChannels({
  workspaceId,
  workspaceMembershipId,
  searchValue,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  searchValue: string;
}) {
  const search = searchValue.length > 0 ? searchValue.toLowerCase() : null;
  const rows: Array<{
    id: string;
    title: string | null;
    unread: boolean | null;
    subscribed: boolean;
    latestItemCreatedAt: string | null;
  }> = [];
  for (const feed of feedsWithPermission(workspaceMembershipId, "read")) {
    if (feed.workspaceId !== workspaceId || feed.isAliasFeed || feed.isDm || feed.title == null) continue;
    if (search !== null && !feed.title.toLowerCase().includes(search)) continue;
    const preference = preferenceForFeed(feed.id, workspaceMembershipId);
    rows.push({
      id: feed.id,
      title: feed.title,
      unread: feedHasUnread(feed.id),
      subscribed: (preference?.pushNotificationPreference ?? "none") !== "none",
      latestItemCreatedAt: latestItemForFeed(feed.id)?.createdAt ?? null,
    });
  }
  rows.sort(
    chainComparators(
      (a, b) => compareDesc(a.unread ?? false, b.unread ?? false),
      (a, b) => compareDesc(a.latestItemCreatedAt ?? NEG_INFINITY, b.latestItemCreatedAt ?? NEG_INFINITY),
    ),
  );
  return rows;
}

export const currentFeedQuery = (currentFeedId: string) => (q: InitialQueryBuilder) =>
  q.from({ feed: store.collection("feed") }).where(({ feed }) => eq(feed.id, currentFeedId));

export type DuplicateAliasAccount = {
  id: string;
  aliasMembershipId: string;
  isAliasFeed: boolean;
  title: string;
  accountId: string;
  name: string;
};

export const fetchDuplicateAliasFeeds = async ({
  feedIds,
  workspaceId,
}: {
  feedIds: string[];
  workspaceId: string;
}): Promise<DuplicateAliasAccount[]> => {
  const rows: DuplicateAliasAccount[] = [];
  for (const feedId of feedIds) {
    const feed = store.get("feed", feedId);
    if (!feed || feed.workspaceId !== workspaceId) continue;
    const membership = store.get("workspaceMembership", feed.aliasMembershipId);
    const account = store.get("account", membership?.accountId);
    if (!membership || !account) continue;
    rows.push({
      id: feed.id,
      aliasMembershipId: feed.aliasMembershipId as string,
      isAliasFeed: feed.isAliasFeed,
      title: feed.title as string,
      accountId: membership.accountId as string,
      name: account.name as string,
    });
  }
  return rows;
};

export const hasFeedsInDatabase = async (workspaceId: string): Promise<{ id: string }[]> => {
  for (const feed of store.getAll("feed").values()) {
    if (feed.workspaceId === workspaceId) return [{ id: feed.id }];
  }
  return [];
};
